import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { MapPin, ArrowRight } from "lucide-react";
import PageHeader from "@/components/PageHeader";
import CTABand from "@/components/CTABand";
import { Reveal } from "@/lib/motion";
import api from "@/lib/api";
import { PROJECT_CATEGORIES, IMAGES } from "@/lib/content";

export default function Projects() {
  const [projects, setProjects] = useState([]);
  const [cat, setCat] = useState("All");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get(`/projects${cat !== "All" ? `?category=${cat}` : ""}`).then((r) => setProjects(r.data)).finally(() => setLoading(false));
  }, [cat]);

  return (
    <div className="relative z-10">
      <PageHeader
        overline="Case Studies"
        titleLines={["Previous", <span key="2" className="text-gold italic">Projects</span>]}
        subtitle="Real transformations across residential, commercial, industrial and land — each a story of energy realigned."
        image={IMAGES.skyline2}
      />

      <section className="py-10 md:py-16">
        <div className="mx-auto max-w-7xl px-6 md:px-8">
          <div className="flex flex-wrap gap-3 mb-12" data-testid="project-filters">
            {PROJECT_CATEGORIES.map((c) => (
              <button
                key={c}
                onClick={() => setCat(c)}
                data-testid={`project-filter-${c.toLowerCase()}`}
                className={`rounded-full px-5 py-2.5 text-sm transition-all duration-300 border ${cat === c ? "bg-primary-green text-white border-primary-green" : "border-white/15 text-[#94A3B8] hover:border-gold/40 hover:text-white"}`}
              >
                {c}
              </button>
            ))}
          </div>

          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {Array.from({ length: 6 }).map((_, i) => <div key={i} className="h-80 rounded-3xl bg-white/[0.03] animate-pulse" />)}
            </div>
          ) : (
            <motion.div layout className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <AnimatePresence mode="popLayout">
                {projects.map((p, i) => (
                  <motion.div
                    layout key={p.id}
                    initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
                    transition={{ duration: 0.5, delay: (i % 3) * 0.06 }}
                  >
                    <Link
                      to={p.internal_link || `/projects/${p.id}`}
                      data-testid={`project-card-${i}`}
                      className="group block text-left overflow-hidden rounded-3xl border border-white/10 transition-all duration-500 hover:border-gold/30 hover:-translate-y-1 h-full"
                    >
                      <div className="relative h-60 overflow-hidden">
                        <img src={p.image} alt={p.image_alt || p.title} className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-110" />
                        <div className="absolute inset-0 bg-gradient-to-t from-[#060A17] to-transparent" />
                        <span className="absolute top-4 left-4 rounded-full bg-primary-green/90 px-3 py-1 text-xs text-white">{p.category}</span>
                        {p.subtype && <span className="absolute top-4 right-4 rounded-full bg-black/40 backdrop-blur px-3 py-1 text-xs text-white/80">{p.subtype}</span>}
                      </div>
                      <div className="p-6">
                        {p.image_caption && <p className="text-xs text-[#94A3B8] mb-2">{p.image_caption}</p>}
                        <h3 className="font-display text-2xl">{p.title}</h3>
                        <p className="text-[#94A3B8] text-sm mt-1 flex items-center gap-1.5"><MapPin size={14} /> {p.location}</p>
                        <span className="mt-4 inline-flex items-center gap-2 text-gold text-sm group-hover:gap-3 transition-all">Read the case study <ArrowRight size={15} /></span>
                      </div>
                    </Link>
                  </motion.div>
                ))}
              </AnimatePresence>
            </motion.div>
          )}
        </div>
      </section>

      <CTABand />
    </div>
  );
}
